import { Mail } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Small round share buttons row.
 * Props:
 *  - title: text to share (page title)
 *  - url: optional override; defaults to window.location.href
 *  - context: optional small suffix like "Foto Galeri" appended to the title
 */
export default function ShareButtons({ title = '', url, context }) {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const fullTitle = context ? `${title} — ${context}` : title;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(fullTitle);
  const encodedText = encodeURIComponent(`${fullTitle}\n\n${shareUrl}`);

  const open = (link) => window.open(link, '_blank', 'noopener,noreferrer');

  const onWhatsApp = () => open(`https://wa.me/?text=${encodedText}`);
  const onFacebook = () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`);
  const onEmail = () => {
    window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedText}`;
  };
  const onInstagram = async () => {
    // Instagram has no public web share endpoint — copy the link to clipboard so user can paste it
    try {
      await navigator.clipboard.writeText(`${fullTitle}\n\n${shareUrl}`);
      toast.success("Bağlantı kopyalandı! Instagram'da paylaşmak için yapıştırın.");
    } catch {
      toast.error('Bağlantı kopyalanamadı. Lütfen elle kopyalayın.');
    }
    open('https://www.instagram.com/');
  };

  const base = 'w-10 h-10 rounded-full flex items-center justify-center text-white shadow-sm hover:shadow-md active:scale-95 transition-all';

  return (
    <div className="flex items-center gap-2.5 flex-wrap" data-testid="share-buttons">
      <span className="text-xs font-medium text-amber-700 mr-1">Paylaş:</span>

      <button
        type="button"
        onClick={onWhatsApp}
        className={`${base} bg-[#25D366] hover:bg-[#1ebe5d]`}
        aria-label="WhatsApp'ta paylaş"
        title="WhatsApp"
        data-testid="share-whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.819 11.819 0 0 1 3.48 8.414c-.003 6.555-5.338 11.891-11.893 11.891a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.371-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
        </svg>
      </button>

      <button
        type="button"
        onClick={onFacebook}
        className={`${base} bg-[#1877F2] hover:bg-[#1462c4]`}
        aria-label="Facebook'ta paylaş"
        title="Facebook"
        data-testid="share-facebook-btn"
      >
        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
          <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"/>
        </svg>
      </button>

      <button
        type="button"
        onClick={onInstagram}
        className={`${base} bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90`}
        aria-label="Instagram'da paylaş"
        title="Instagram (bağlantı kopyalanır)"
        data-testid="share-instagram-btn"
      >
        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </button>

      <button
        type="button"
        onClick={onEmail}
        className={`${base} bg-amber-600 hover:bg-amber-700`}
        aria-label="E-posta ile paylaş"
        title="E-posta"
        data-testid="share-email-btn"
      >
        <Mail className="w-[18px] h-[18px]" />
      </button>
    </div>
  );
}
